import { StorageLocationProduct } from '../types/storage-articles';
import {
  computed,
  effect,
  inject,
  Injectable,
  Injector,
  linkedSignal,
  resource,
  ResourceStatus,
  Signal,
  signal,
} from '@angular/core';
import { StorageLocationProductRepository } from '../repositories/storage-location-product.repository';
import { handleRequestError } from '@softline/application';
import { MessageBarService } from '@softline/ui-core';
import { StorageLocationProductResponse } from '../types/storage-location-product-response';

@Injectable()
export class StorageLocationProductService {
  readonly #storageLocationId = signal<Signal<number | null>>(signal(null));
  readonly #positionMarker = signal<number>(-1);

  readonly #messageBarService = inject(MessageBarService);
  readonly #storageLocationRepository = inject(StorageLocationProductRepository);
  readonly #injector = inject(Injector);

  readonly pageSize = signal(50);
  readonly page = signal(1);

  private readonly limit = computed(() => {
    return this.pageSize();
  });

  private readonly offset = computed(() => {
    return (this.page() - 1) * this.pageSize();
  });

  readonly #productResource = resource({
    request: () => {
      const locationId = this.#storageLocationId()();

      if (!locationId) return undefined;

      return {
        locationId,
        limit: this.limit(),
        offset: this.offset(),
      };
    },
    loader: ({ request }) => {
      if (!request?.locationId) return Promise.resolve(null);

      const { locationId, ...paginationQuery } = request;

      console.log(
        '[StorageLocationProductService] storageLocationId changed:',
        locationId,
        paginationQuery
      );

      try {
        return this.#storageLocationRepository.getStorageLocationProducts(
          locationId,
          paginationQuery
        );
      } catch (e) {
        handleRequestError(e, this.#injector, { show: true });
        return Promise.resolve(null);
      }
    },
  });

  readonly memoizedResourceValue = linkedSignal<
    any,
    StorageLocationProductResponse | null | undefined
  >({
    source: () => ({
      value: this.#productResource.value(),
      status: this.#productResource.status(),
    }),
    computation: (source, previous) => {
      if (previous && source.status === ResourceStatus.Loading) {
        return previous.value;
      }
      return source.value ?? [];
    },
  });

  readonly isLoadingProducts = computed(
    () =>
      this.#productResource.status() === ResourceStatus.Loading ||
      this.#productResource.status() === ResourceStatus.Idle
  );

  readonly #products = linkedSignal(
    () => this.memoizedResourceValue()?.storageLocationProductResponses ?? []
  );

  readonly total = computed(() => this.memoizedResourceValue()?.totalCount);

  readonly products = computed(() => this.#products());
  readonly positionMarker = computed(() => this.#positionMarker());

  register(signal: Signal<number | null>): void {
    this.#storageLocationId.set(signal);
  }

  setPage(page: number): void {
    this.page.set(page);
  }

  setPageSize(size: number): void {
    this.pageSize.set(size);
  }

  constructor() {
    effect(() => {
      console.log(this.#productResource.value());
    });
  }

  async moveProduct(
    idStorage: number,
    products: StorageLocationProduct[]
  ): Promise<void> {
    try {
      const value = await this.#storageLocationRepository.move(idStorage, products);

      this.#products.set(value);
    } catch (e) {
      handleRequestError(e, this.#injector, { show: true });
    }
  }

  async updateProduct(idStorage: number, product: StorageLocationProduct): Promise<void> {
    try {
      const value = await this.#storageLocationRepository.update(
        idStorage,
        product.id,
        product
      );
      const products = [...this.#products()];

      const index = products.findIndex((p) => p.id === value.id);

      if (index < 0) return;

      products[index] = value;

      this.#products.set(products);

      this.#messageBarService.success('Der Artikel wurde erfolgreich bearbeitet.');
    } catch (e) {
      handleRequestError(e, this.#injector, { show: true });
    }
  }

  async deleteAllProducts(idStorage: number): Promise<void> {
    try {
      await this.#storageLocationRepository.deleteAll(idStorage);

      this.#products.set([]);

      this.#messageBarService.success(
        'Es wurden alle Artikel von diesem Lagerplatz entfernt.'
      );
    } catch (e) {
      handleRequestError(e, this.#injector, { show: true });
    }
  }

  async deleteProduct(idStorage: number, idProduct: number): Promise<void> {
    try {
      await this.#storageLocationRepository.delete(idStorage, idProduct);

      const products = [...this.#products()];

      const deletedProduct = products.find((p) => p.id === idProduct);

      if (!deletedProduct) return;

      const newProducts = products.filter((p) => p.id !== idProduct);

      this.#products.set(
        newProducts.map((p) => {
          if (p.position > deletedProduct.position) {
            return {
              ...p,
              position: p.position - 1,
            };
          } else {
            return p;
          }
        })
      );

      this.#messageBarService.success('Dieser Artikel wurde erfolgreich entfernt.');
    } catch (e) {
      handleRequestError(e, this.#injector, { show: true });
    }
  }

  async addProduct(idStorage: number, idProduct: number): Promise<void> {
    const products = this.products();

    const productExists = products.find((p) => p.product.id === idProduct);

    if (productExists) {
      this.#messageBarService.info(
        'Dieser Artikel wurde schon zum Lagerplatz hinzugefügt.'
      );
      return;
    }

    try {
      let position = this.positionMarker();

      if (position < 1) {
        position = products.length > 0 ? products.length + 1 : 1;
      }

      const body = {
        position,
        product: {
          id: idProduct,
        },
      };

      const result = await this.#storageLocationRepository.add(idStorage, body);

      this.#products.set(result);
      this.setPositionMarker(position + 1);

      this.#messageBarService.success('Der Artikel wurde erfolgreich hinzugefügt.');
    } catch (e) {
      handleRequestError(e, this.#injector, { show: true });
    }
  }

  setPositionMarker(position: number) {
    this.#positionMarker.set(position);
  }

  resetPositionMarker() {
    this.#positionMarker.set(-1);
  }
}
